import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { visagistThemes, type Visagist } from "@/data/visagists";
import { fetchVisagistBySlug } from "@/lib/visagists-source";
import { PortraitImage } from "@/components/PortraitImage";
import { FoundingMemberBadge } from "@/components/FoundingMemberBadge";
import { CreatorSelectedSection } from "@/components/CreatorSelectedSection";
import { fetchSelectedForCreator, type CreatorSelectedImage } from "@/lib/community-submissions";
import { sameImageUrl } from "@/lib/image-url";
import { supabase } from "@/integrations/supabase/client";
import { PhotoLightbox, type LightboxInfo } from "@/components/PhotoLightbox";

export const Route = createFileRoute("/visagists/$id")({
  loader: async ({ params }) => {
    const visagist = await fetchVisagistBySlug(params.id);
    if (!visagist) throw notFound();
    const { data: ids } = await supabase
      .from("visagists")
      .select("user_id")
      .eq("slug", params.id)
      .maybeSingle();
    const selected = await fetchSelectedForCreator({ userId: ids?.user_id ?? null });
    return { visagist, selected };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.visagist;
    const descBase = p ? `${p.name}, Visagist:in aus ${p.region}. ${p.bio}`.replace(/\s+/g, " ").trim() : "";
    const description = descBase.length > 160 ? descBase.slice(0, 157).trimEnd() + "…" : descBase;
    return {
      meta: p
        ? [
            { title: `${p.name}, OM Selected` },
            { name: "description", content: description },
            { property: "og:title", content: `${p.name}, OM Selected` },
            { property: "og:description", content: p.bio.slice(0, 160) },
            { property: "og:image", content: p.portrait },
            { property: "og:url", content: `https://omselected.art/visagists/${p.id}` },
          ]
        : [{ title: "Visagist:in, OM Selected" }],
      links: p ? [{ rel: "canonical", href: `https://omselected.art/visagists/${p.id}` }] : undefined,
      scripts: p
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ProfilePage",
                url: `https://omselected.art/visagists/${p.id}`,
                inLanguage: "de-DE",
                mainEntity: {
                  "@type": "Person",
                  name: p.name,
                  description: p.bio,
                  image: p.portrait,
                  jobTitle: "Visagist",
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: p.city ?? p.region,
                    addressCountry: p.country ?? undefined,
                  },
                  email: p.email || undefined,
                  url: p.websiteUrl ?? undefined,
                  sameAs: [
                    p.websiteUrl,
                    p.instagramHandle ? `https://instagram.com/${p.instagramHandle}` : null,
                  ].filter(Boolean),
                },
              }),
            },
          ]
        : undefined,
    };
  },
  notFoundComponent: () => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">{t("profile.notFound")}</h1>
        <Link to="/visagists" className="eyebrow inline-block mt-8 border-b border-foreground pb-1">{t("profile.back")}</Link>
      </div>
    );
  },
  errorComponent: ({ error }) => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">{t("profile.error")}</h1>
        <p className="mt-4 text-muted-foreground">{error.message}</p>
      </div>
    );
  },
  component: VisagistDetail,
});

function VisagistDetail() {
  const { t } = useTranslation();
  const { visagist: p, selected } = Route.useLoaderData() as { visagist: Visagist; selected: CreatorSelectedImage[] };
  const [lightbox, setLightbox] = useState<{ items: LightboxInfo[]; index: number } | null>(null);
  const [galleryFilter, setGalleryFilter] = useState<string>("all");

  const makeInfo = (it: { src: string; theme: string }): LightboxInfo => ({
    image: it.src,
    caption: null,
    photographerName: p.name,
    photographerId: null,
    region: p.city ?? p.region,
    theme: visagistThemes.find((th) => th.id === it.theme)?.label ?? null,
    instagramHandle: p.instagramHandle ?? null,
    websiteUrl: p.websiteUrl ?? null,
    creatorType: "visagist" as const,
  });

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-10 pb-16 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <PortraitImage p={p} loading="eager" />
          </div>
          <div className="md:col-span-7 flex flex-col justify-between">
            <div>
              <Link to="/visagists" className="eyebrow text-muted-foreground hover:text-foreground">{t("profile.backVisagists")}</Link>
              <div className="flex items-center gap-4 mt-6">
                <h1 className="serif text-5xl md:text-7xl leading-[0.95]">{p.name}</h1>
                {p.foundingMember && <FoundingMemberBadge />}
              </div>
              <div className="eyebrow text-muted-foreground mt-6">
                {p.themes.map((t) => visagistThemes.find((th) => th.id === t)?.label).join(" · ")} · {p.city ?? p.region}{p.country ? `, ${p.country}` : ""}
              </div>
              <p className="serif italic text-xl md:text-2xl mt-10 leading-snug max-w-xl">{p.bio}</p>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-6 eyebrow text-muted-foreground border-t border-border pt-6">
              {(p.priceFrom > 0 || p.tfp || p.priceOnRequest) && (
                <div>
                  <div>{t("profile.priceFrom")}</div>
                  <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">
                    {[
                      p.priceFrom > 0 ? `${p.priceFrom.toLocaleString("de-DE")} €` : null,
                      p.tfp ? "TFP" : null,
                      p.priceOnRequest ? t("profile.onRequest") : null,
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
              )}
              <div>
                <div>{t("profile.location")}</div>
                <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">
                  {[p.city ?? p.region, p.country].filter(Boolean).join(", ") || t("profile.onRequest")}
                </div>
              </div>
              {p.email && (
                <div>
                  <div>{t("profile.contact")}</div>
                  <a href={`mailto:${p.email}`} className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal block">{p.email}</a>
                </div>
              )}
              {(p.shootingSettings?.length ?? 0) > 0 && (
                <div>
                  <div>{t("profile.setting")}</div>
                  <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">{p.shootingSettings!.join(", ")}</div>
                </div>
              )}
              {(p.languages?.length ?? 0) > 0 && (
                <div>
                  <div>{t("profile.languages")}</div>
                  <div className="serif italic text-foreground text-lg mt-1 normal-case tracking-normal">{p.languages!.join(", ")}</div>
                </div>
              )}
            </div>
            {(p.websiteUrl || p.instagramHandle) && (
              <div className="mt-8 flex flex-wrap gap-3 eyebrow">
                {p.websiteUrl && (
                  <a href={p.websiteUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors">
                    <span aria-hidden>↗</span> {t("profile.website")}
                  </a>
                )}
                {p.instagramHandle && (
                  <a href={`https://instagram.com/${p.instagramHandle}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors">
                    <span aria-hidden>◎</span> @{p.instagramHandle}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20">
          <div className="eyebrow text-muted-foreground mb-8">{t("profile.gallery")}</div>
          {(() => {
            const tags = p.galleryThemes ?? [];
            const galleryItems = p.gallery.map((src, i) => ({ src, theme: tags[i] ?? "open", index: i }));
            const presentThemeIds = visagistThemes
              .map((th) => th.id)
              .filter((id) => galleryItems.some((it) => it.theme === id));
            const selectedGalleryItems = galleryItems.filter((it) =>
              selected.some((s) => sameImageUrl(s.image_url, it.src))
            );
            const hasSelectedFilter = selectedGalleryItems.length > 0;
            const visibleItems = galleryFilter === "all"
              ? galleryItems
              : galleryFilter === "selected"
              ? selectedGalleryItems
              : galleryItems.filter((it) => it.theme === galleryFilter);
            const showFilters = galleryItems.length > 0;
            return (
              <>
                {showFilters && (
                  <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label={t("profile.gallery")}>
                    <button
                      type="button"
                      onClick={() => setGalleryFilter("all")}
                      aria-pressed={galleryFilter === "all"}
                      className={`eyebrow border px-4 py-2 transition-colors ${galleryFilter === "all" ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}
                    >
                      {t("profile.filterAll")}
                    </button>
                    {hasSelectedFilter && (
                      <button
                        type="button"
                        onClick={() => setGalleryFilter("selected")}
                        aria-pressed={galleryFilter === "selected"}
                        className={`eyebrow border px-4 py-2 transition-colors ${galleryFilter === "selected" ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}
                      >
                        {t("profile.filterSelected")}
                      </button>
                    )}
                    {presentThemeIds.map((id) => {
                      const label = visagistThemes.find((th) => th.id === id)?.label ?? id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setGalleryFilter(id)}
                          aria-pressed={galleryFilter === id}
                          className={`eyebrow border px-4 py-2 transition-colors ${galleryFilter === id ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                  {visibleItems.map((it, idx) => (
                    <button key={it.index} type="button" onClick={() => setLightbox({ items: visibleItems.map(makeInfo), index: idx })} className="block w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground" aria-label={t("profile.zoomAria", { n: it.index + 1 })}>
                      <img src={it.src} alt={`${p.name}, ${it.index + 1}`} loading="lazy" className="w-full aspect-[4/5] object-cover cursor-zoom-in no-bw" />
                    </button>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </section>

      <CreatorSelectedSection items={selected} name={p.name} creatorType="visagist" />

      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 text-center">
          <h3 className="serif text-3xl md:text-4xl">{t("profile.workWith", { name: p.name.split(" ")[0] })}</h3>
          <div className="mt-8 flex justify-center gap-3 eyebrow">
            <a href={`mailto:${p.email}`} className="border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors">{t("profile.writeDirect")}</a>
            <Link to="/brief" className="border border-border px-5 py-3 hover:border-foreground transition-colors">{t("profile.sendRequest")}</Link>
          </div>
        </div>
      </section>

      <PhotoLightbox
        info={lightbox ? lightbox.items[lightbox.index] : null}
        onClose={() => setLightbox(null)}
        onPrev={lightbox && lightbox.index > 0 ? () => setLightbox((s) => (s ? { ...s, index: s.index - 1 } : s)) : undefined}
        onNext={lightbox && lightbox.index < lightbox.items.length - 1 ? () => setLightbox((s) => (s ? { ...s, index: s.index + 1 } : s)) : undefined}
        neighborImages={lightbox ? [lightbox.items[lightbox.index - 1]?.image, lightbox.items[lightbox.index + 1]?.image] : undefined}
      />
    </div>
  );
}